import { useEffect, useMemo, useState } from "react";
import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import CustomTinyMceEditor from "../../components/shared/Inputs/CustomTinyMceEditor";
import useTemplateEditor from "../../data/useTemplateEditor";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ITemplateEditorParentTemplate, TemplateType } from "../../models/TemplateEditor";

interface ITemplateDraft {
  title: string;
  body: string;
}

const getTemplateTypeLabel = (type: TemplateType) => {
  switch (type) {
    case TemplateType.Email:
      return "Email";
    case TemplateType.Message:
      return "Message";
    case TemplateType.Product:
      return "Product";
    default:
      return "Template";
  }
};

export default function TemplateEditor() {
  const { saveHandlers } = useDefaultCRUDHandlers("Template");
  const {
    templateEditor,
    templates,
    availableParents,
    createChildTemplate,
    updateChildTemplate,
    resetChildTemplate,
    deleteChildTemplate
  } = useTemplateEditor();

  const [selectedParentId, setSelectedParentId] = useState("");
  const [drafts, setDrafts] = useState<Record<string, ITemplateDraft>>({});

  useEffect(() => {
    const nextDrafts: Record<string, ITemplateDraft> = {};
    templates.forEach((template) => {
      if (!template.childTemplate) return;
      nextDrafts[template.childTemplate.id] = {
        title: template.childTemplate.title,
        body: template.childTemplate.body,
      };
    });
    setDrafts(nextDrafts);
  }, [templates]);

  const selectedParent = useMemo(() => {
    return availableParents.find((template) => template.id === selectedParentId);
  }, [availableParents, selectedParentId]);

  const updateDraft = (childTemplateId: string, patch: Partial<ITemplateDraft>) => {
    setDrafts((previous) => ({
      ...previous,
      [childTemplateId]: {
        ...previous[childTemplateId],
        ...patch,
      },
    }));
  };

  const handleCreateChild = async () => {
    if (!selectedParent) return;

    try {
      await createChildTemplate.mutateAsync({
        parentTemplateId: selectedParent.id,
        request: {
          type: selectedParent.type,
          title: selectedParent.title,
          body: selectedParent.body,
          parentTemplateId: selectedParent.id,
        },
      });
      setSelectedParentId("");
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleSaveChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    const draft = drafts[template.childTemplate.id] || {
      title: template.childTemplate.title,
      body: template.childTemplate.body,
    };

    try {
      await updateChildTemplate.mutateAsync({
        childTemplateId: template.childTemplate.id,
        request: {
          type: template.type,
          title: draft.title,
          body: draft.body,
          parentTemplateId: template.id,
        },
      });
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleResetChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    try {
      await resetChildTemplate.mutateAsync(template.childTemplate.id);
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const handleRemoveChild = async (template: ITemplateEditorParentTemplate) => {
    if (!template.childTemplate) return;

    try {
      await deleteChildTemplate.mutateAsync(template.childTemplate.id);
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  if (templateEditor.isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Template Editor
        </h1>
        <p className="text-gray-600">
          Create one custom child template per parent template. Parent templates stay as source templates and can reset child content.
        </p>
      </div>

      {Boolean(templateEditor.error) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          Failed to load templates
        </div>
      )}

      <Card title="Add Custom Template" containerClassName="mb-6" bodyClassName="p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="w-full sm:max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Template</label>
            <select
              className="w-full rounded-md border border-gray-300 text-sm"
              value={selectedParentId}
              onChange={(e) => setSelectedParentId(e.target.value)}
            >
              <option value="">Select template...</option>
              {availableParents.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title} ({getTemplateTypeLabel(template.type)})
                </option>
              ))}
            </select>
          </div>
          <ButtonInput
            label="Add Custom Template"
            onClick={handleCreateChild}
            disabled={!selectedParentId || createChildTemplate.isLoading}
            isLoading={createChildTemplate.isLoading}
            classes="py-2.5"
          />
        </div>
      </Card>

      {templates.map((template) => {
        const mode = template.type === TemplateType.Message ? "text" : "html";
        const child = template.childTemplate;
        const draft = child ? (drafts[child.id] || { title: child.title, body: child.body }) : null;
        const isChildMutating =
          (updateChildTemplate.isLoading && updateChildTemplate.variables?.childTemplateId === child?.id) ||
          (resetChildTemplate.isLoading && resetChildTemplate.variables === child?.id) ||
          (deleteChildTemplate.isLoading && deleteChildTemplate.variables === child?.id);

        return (
          <Card
            key={template.id}
            title={`${template.title} (${getTemplateTypeLabel(template.type)})`}
            containerClassName="mb-6"
            bodyClassName="p-6"
          >
            <div className="space-y-4">
              {child ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Template Title</label>
                    <input
                      type="text"
                      value={draft?.title || ""}
                      onChange={(e) => updateDraft(child.id, { title: e.target.value })}
                      className="w-full rounded-md border border-gray-300 text-sm"
                    />
                  </div>

                  <CustomTinyMceEditor
                    content={draft?.body || ""}
                    setContent={(value) => updateDraft(child.id, { body: value })}
                    mode={mode}
                    mergeTags={template.mergeTags}
                    placeholder={`Edit ${getTemplateTypeLabel(template.type).toLowerCase()} template...`}
                    height={420}
                  />

                  <div className="flex flex-wrap gap-3 justify-end">
                    <ButtonInput
                      label="Remove Custom Template"
                      onClick={() => handleRemoveChild(template)}
                      disabled={isChildMutating}
                      isLoading={deleteChildTemplate.isLoading && deleteChildTemplate.variables === child.id}
                      classes="bg-red-600 hover:bg-red-700"
                    />
                    <ButtonInput
                      label="Reset To Parent"
                      onClick={() => handleResetChild(template)}
                      disabled={isChildMutating}
                      isLoading={resetChildTemplate.isLoading && resetChildTemplate.variables === child.id}
                      classes="bg-gray-600 hover:bg-gray-700"
                    />
                    <ButtonInput
                      label="Save Custom Template"
                      onClick={() => handleSaveChild(template)}
                      disabled={isChildMutating}
                      isLoading={updateChildTemplate.isLoading && updateChildTemplate.variables?.childTemplateId === child.id}
                      classes=""
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">No custom template created yet.</p>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
